import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Mail,
  FileText,
  Loader2,
  CheckCircle,
  Edit3,
  Save,
  Sparkles,
  Copy,
  Download,
  RefreshCw,
  X
} from "lucide-react";

const TONE_OPTIONS = [
  { value: "professional", label: "Professional & Formal", description: "Polished and corporate-appropriate" },
  { value: "confident", label: "Confident & Assertive", description: "Bold and self-assured" },
  { value: "conversational", label: "Conversational & Warm", description: "Friendly and personable" },
  { value: "creative", label: "Creative & Dynamic", description: "Unique and engaging" },
  { value: "concise", label: "Concise & Direct", description: "Straight to the point" }
];

export const CoverLetterTailorModal = ({ job, application, onClose, onGenerated }) => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("none");
  const [tone, setTone] = useState("professional");
  const [loading, setLoading] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [coverLetterId, setCoverLetterId] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [newTemplate, setNewTemplate] = useState({ name: "", content: "" });
  const [savingTemplate, setSavingTemplate] = useState(false);

  useEffect(() => {
    fetchTemplates();
    checkExistingCoverLetter();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/cover-letter-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const checkExistingCoverLetter = async () => {
    try {
      const response = await axios.get(
        `${API}/applications/${application.id}/cover-letter/check`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.has_cover_letter) {
        // Load existing cover letter
        const clResponse = await axios.get(
          `${API}/applications/${application.id}/cover-letter`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setGeneratedContent(clResponse.data.content);
        setEditedContent(clResponse.data.content);
        setCoverLetterId(clResponse.data.id);
        setTone(clResponse.data.tone || "professional");
      }
    } catch (error) {
      // No existing cover letter, that's fine
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/cover-letters/generate`,
        {
          job_id: job.id,
          application_id: application.id,
          tone: tone,
          template_id: selectedTemplateId !== "none" ? selectedTemplateId : null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setGeneratedContent(response.data.content);
      setEditedContent(response.data.content);
      setCoverLetterId(response.data.id);
      toast.success("Cover letter generated successfully!");
      
      if (onGenerated) {
        onGenerated(response.data);
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to generate cover letter";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!coverLetterId) return;

    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/cover-letters/${coverLetterId}`,
        { content: editedContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedContent(editedContent);
      setIsEditing(false);
      toast.success("Changes saved!");
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      toast.success("Copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const handleDownloadPdf = async () => {
    if (!coverLetterId) {
      toast.error("No cover letter to download");
      return;
    }
    
    try {
      toast.info("Generating PDF...");
      const response = await axios.get(
        `${API}/cover-letters/${coverLetterId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cover_Letter_${job?.company || 'Job'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error("Failed to download PDF");
    }
  };

  const handleDownloadTxt = () => {
    const blob = new Blob([generatedContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `cover_letter_${job?.company || "job"}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Text file downloaded!");
  };

  const handleSaveTemplate = async () => {
    if (!newTemplate.content.trim()) {
      toast.error("Please enter template content");
      return;
    }

    setSavingTemplate(true);
    try {
      const response = await axios.post(
        `${API}/cover-letter-templates`,
        {
          name: newTemplate.name || "My Template",
          content: newTemplate.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplates([response.data, ...templates.filter(t => t.id !== response.data.id)]);
      setSelectedTemplateId(response.data.id);
      setShowTemplateEditor(false);
      setNewTemplate({ name: "", content: "" });
      toast.success("Template saved!");
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!generatedContent) return;
    
    setSavingTemplate(true);
    try {
      const response = await axios.post(
        `${API}/cover-letter-templates`,
        {
          name: `Template from ${job?.company || "Job"}`,
          content: generatedContent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTemplates([response.data, ...templates.filter(t => t.id !== response.data.id)]);
      toast.success("Saved as template!");
    } catch (error) {
      toast.error("Failed to save as template");
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="cover-letter-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Mail className="h-5 w-5 text-[#A3C3D8]" />
            Generate Tailored Cover Letter
          </DialogTitle>
          <DialogDescription>
            Create a personalized cover letter for this job application
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Job Info */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-[#2D3748] mb-1">{job?.title}</h3>
            <p className="text-sm text-[#94A3B8]">{job?.company}</p>
          </div>

          {!generatedContent ? (
            /* Configuration Section */
            <div className="space-y-6">
              {/* Template Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-[#555F67] flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Base Template (Optional)
                  </Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowTemplateEditor(true)}
                    className="text-[#4A728D] border-[#A3C3D8]"
                    data-testid="create-template-btn"
                  >
                    <Edit3 className="h-4 w-4 mr-1" />
                    Create Template
                  </Button>
                </div>
                
                {loadingTemplates ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-[#A3C3D8]" />
                  </div>
                ) : (
                  <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                    <SelectTrigger data-testid="template-select">
                      <SelectValue placeholder="Select a template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-[#A3C3D8]" />
                          Generate from scratch
                        </div>
                      </SelectItem>
                      {templates.map(template => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-[#94A3B8]" />
                            {template.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                
                <p className="text-xs text-[#94A3B8]">
                  A template helps maintain your preferred style and structure. The AI will personalize it for this job.
                </p>
              </div>

              {/* Tone Selection */}
              <div className="space-y-2">
                <Label className="text-[#555F67]">Writing Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONE_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex flex-col">
                          <span>{option.label}</span>
                          <span className="text-xs text-[#94A3B8]">{option.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Generate Button */}
              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38] h-12"
                data-testid="generate-cover-letter-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Generating Cover Letter...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    Generate Cover Letter
                  </>
                )}
              </Button>

              {/* Template Editor Modal */}
              {showTemplateEditor && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-[#2D3748]">Create Cover Letter Template</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowTemplateEditor(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <Label>Template Name</Label>
                        <input
                          type="text"
                          value={newTemplate.name}
                          onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                          placeholder="e.g., My Professional Template"
                          className="w-full mt-1 px-3 py-2 border rounded-md"
                        />
                      </div>
                      
                      <div>
                        <Label>Template Content</Label>
                        <p className="text-xs text-[#94A3B8] mb-2">
                          Write your base cover letter. The AI will use this as a starting point and personalize it for each job.
                        </p>
                        <Textarea
                          value={newTemplate.content}
                          onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                          placeholder="Dear Hiring Manager,

I am writing to express my interest in the [Position] role at [Company]. With my background in...

[Your template content here - the AI will personalize this for each job]

Sincerely,
[Your Name]"
                          className="min-h-[300px] font-mono text-sm"
                        />
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowTemplateEditor(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveTemplate}
                          disabled={savingTemplate || !newTemplate.content.trim()}
                          className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                        >
                          {savingTemplate ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-1" />
                              Save Template
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Results Section */
            <div className="space-y-6">
              {/* Success Banner */}
              <div className="bg-green-50 rounded-lg p-4 flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-medium text-green-800">Cover Letter Generated</p>
                  <p className="text-sm text-green-600">Personalized for {job?.company}</p>
                </div>
              </div>

              {/* Cover Letter Content */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-[#555F67]">Your Cover Letter</Label>
                  {!isEditing ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditing(true)}
                      className="text-[#4A728D]"
                      data-testid="edit-cover-letter-btn"
                    >
                      <Edit3 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setIsEditing(false);
                          setEditedContent(generatedContent);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSaveEdit}
                        disabled={savingEdit}
                        className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                        data-testid="save-edit-btn"
                      >
                        {savingEdit ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-1" />
                            Save
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
                
                {isEditing ? (
                  <Textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="min-h-[400px] font-mono text-sm"
                    data-testid="cover-letter-editor"
                  />
                ) : (
                  <div 
                    className="bg-white border rounded-lg p-4 max-h-[400px] overflow-y-auto whitespace-pre-wrap text-sm text-[#555F67]"
                    data-testid="cover-letter-preview"
                  >
                    {generatedContent}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap justify-between gap-4 pt-4 border-t">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setGeneratedContent(null);
                      setCoverLetterId(null);
                    }}
                    className="border-slate-200"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Regenerate
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCopy}
                    className="border-[#A3C3D8] text-[#4A728D] hover:bg-[#A3C3D8]/10"
                    data-testid="copy-cover-letter-btn"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadPdf}
                    className="border-[#A3C3D8] text-[#4A728D] hover:bg-[#A3C3D8]/10"
                    data-testid="download-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTxt}
                    className="border-slate-200 text-[#555F67] hover:bg-slate-50"
                    data-testid="download-txt-btn"
                    title="Download as plain text"
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    TXT
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleSaveAsTemplate}
                    disabled={savingTemplate}
                    className="border-slate-200 text-[#555F67]"
                  >
                    {savingTemplate ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-1" />
                        Save as Template
                      </>
                    )}
                  </Button>
                </div>
                <Button
                  onClick={onClose}
                  className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
                  data-testid="done-btn"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CoverLetterTailorModal;
